<template>
  <div class="activity-tabs">
    <FriedSelect
      v-if="isXSmallScreenAndDown"
      class="select-activity-tabs"
      :model-value="selectedTab"
      :options="tabs"
      option-label="label"
      option-key="key"
      @update:model-value="selectTab"
    />
    <FriedTab
      v-for="tab in tabs"
      v-else
      :key="tab.key"
      :model-value="selectedTab"
      :value="tab.key"
      :data-external="tab.dataExternal"
      class="activity-tab"
      type="primary"
      @update:model-value="selectTab"
    >
      <div class="tab-content">
        <FriedParagraph>{{ tab.label }}</FriedParagraph>
        <NotificationBadge
          v-if="!!getUnreadNotifications(tab.key)"
          :active="selectedTab === tab.key"
          :notification-count="getUnreadNotifications(tab.key)"
        />
      </div>
    </FriedTab>
  </div>
</template>

<script lang="ts">
import type { ComputedRef } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { ActivityTab } from '../enums/activity-tabs';
import type { ActivityTabItem } from '../types/activity-tab-item';
import NotificationBadge from './NotificationBadge.vue';

export default defineComponent({
  components: {
    NotificationBadge,
  },
  props: {
    unreadChatCount: {
      type: Number,
      default: 0,
    },
    unreadCommentsCount: {
      type: Number,
      default: 0,
    },
    unreadTotalCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['select-tab'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { isXSmallScreenAndDown } = useBreakpoints();
    const selectedTab = ref(ActivityTab.All);

    const unreadOtherNotificationsCount = computed(
      () => props.unreadTotalCount - props.unreadChatCount - props.unreadCommentsCount
    );

    const selectTab = (key: ActivityTab) => {
      selectedTab.value = key;
      emit('select-tab', key);
    };

    const tabs: ComputedRef<ActivityTabItem[]> = computed(() => [
      {
        label: isXSmallScreenAndDown.value
          ? t('dr_activity_sheet_all_activity_number', { number: props.unreadTotalCount })
          : t('activity_sheet_all_activity'),
        key: ActivityTab.All,
        dataExternal: `activity-tab-button-${ActivityTab.All}`,
      },
      {
        label: isXSmallScreenAndDown.value
          ? t('dr_activity_sheet_chat_number', { number: props.unreadChatCount })
          : t('activity_sheet_chat'),
        key: ActivityTab.Chat,
        dataExternal: `activity-tab-button-${ActivityTab.Chat}`,
      },
      {
        label: isXSmallScreenAndDown.value
          ? t('dr_activity_sheet_comments_number', { number: props.unreadCommentsCount })
          : t('activity_sheet_comments'),
        key: ActivityTab.Comments,
        dataExternal: `activity-tab-button-${ActivityTab.Comments}`,
      },
      {
        label: isXSmallScreenAndDown.value
          ? t('dr_activity_sheet_other_number', {
              number: unreadOtherNotificationsCount.value,
            })
          : t('activity_sheet_other'),
        key: ActivityTab.Other,
        dataExternal: `activity-tab-button-${ActivityTab.Other}`,
      },
    ]);

    const getUnreadNotifications = (tab: ActivityTab) => {
      switch (tab) {
        case ActivityTab.All:
          return props.unreadTotalCount;
        case ActivityTab.Chat:
          return props.unreadChatCount;
        case ActivityTab.Comments:
          return props.unreadCommentsCount;
        case ActivityTab.Other:
          return unreadOtherNotificationsCount.value;
        default:
          return 0;
      }
    };

    return {
      tabs,
      selectedTab,
      isXSmallScreenAndDown,
      getUnreadNotifications,
      selectTab,
      t,
    };
  },
});
</script>

<style lang="scss" scoped>
.activity-tabs {
  grid-column: 1 / span 2;
  margin-top: var(--spacing-75);

  .select-activity-tabs {
    width: 100%;
    margin-bottom: var(--spacing-100);
  }

  .activity-tab {
    width: 25%;
    height: fit-content;
    border-bottom: 1px solid var(--gray-89);

    .tab-content {
      display: flex;
      gap: var(--spacing-50);
      justify-content: center;

      p {
        text-wrap: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
